/**
 * On the marketing host the home page is the root, as it was on Webflow. The
 * route that renders it stays `/home`, because `/` on every other host is the
 * app shell that sends a visitor into the dashboard, and one route cannot be
 * both. The two are joined where the address is read and written, outside the
 * router, so the router is configured the same on every host:
 *
 * - the server hands the root of `www` to the Start handler as `/home`
 *   (`homeRouteUrl`, in `src/server.ts`), as the static server answers it
 *   with the page the prerender crawl wrote for `/home`;
 * - the browser's history on `www` reads the address `/` as `/home` and
 *   writes `/home` as `/` (`marketingHomeHistory`), so the router matches the
 *   page it was sent and the address bar keeps the root.
 *
 * Not a router `rewrite`. With one configured, router-core keeps the address
 * as it was asked for and, on the server, redirects whenever it differs from
 * the address the router rebuilds. Start hands the router a query re-encoded
 * with `URLSearchParams` (a space as `+`), our `stringifySearch` rebuilds it
 * with a space as `%20`, and the two never agree: every request with a space
 * in its query was answered 307 to itself, on every host, Google sign-in's
 * return included (`scope=email+profile+openid`). Without a rewrite the router
 * compares two rebuilt addresses, which always agree.
 *
 * `/home` asked for by address on `www` is sent to `/` with the 301 the
 * published site gave (`marketingRedirects.ts`).
 */
import {
  createBrowserHistory,
  createMemoryHistory,
  type HistoryLocation,
  type RouterHistory,
} from "@tanstack/react-router";

import { MARKETING_HOST } from "./hosts";

const HOME_ROUTE = "/home";

/** Whether `href` is `path` itself, or `path` with a query or a fragment. */
const isAddressOf = (href: string, path: string) =>
  href === path || href.startsWith(`${path}?`) || href.startsWith(`${path}#`);

/** The route the marketing host matches an address as: its root is the home. */
function routeAddress(href: string): string {
  return isAddressOf(href, "/") ? `${HOME_ROUTE}${href.slice(1)}` : href;
}

/** The address the marketing host shows for a route: the home is its root. */
function publicAddress(href: string): string {
  return isAddressOf(href, HOME_ROUTE)
    ? `/${href.slice(HOME_ROUTE.length)}`
    : href;
}

/**
 * `href` parsed as TanStack's own browser history parses the address bar,
 * with the entry's history state when it has one.
 */
function parsedLocation(
  href: string,
  state: HistoryLocation["state"] | null
): HistoryLocation {
  const { location } = createMemoryHistory({ initialEntries: [href] });
  return state ? { ...location, state } : location;
}

/**
 * The browser history on the marketing host: TanStack's own, reading the root
 * as the home route and writing the home route as the root. None anywhere
 * else, the server included, where the router keeps its default.
 */
export function marketingHomeHistory(): RouterHistory | undefined {
  if (
    typeof window === "undefined" ||
    window.location.host !== MARKETING_HOST
  ) {
    return undefined;
  }
  return createBrowserHistory({
    parseLocation: () => {
      const { pathname, search, hash } = window.location;
      return parsedLocation(
        routeAddress(`${pathname}${search}${hash}`),
        window.history.state
      );
    },
    createHref: publicAddress,
  });
}

/**
 * The address the Start handler renders for a request to the marketing host's
 * root: the home route, with the query asked for. Null for any other request.
 */
export function homeRouteUrl(host: string | null, url: URL): URL | null {
  return host === MARKETING_HOST && url.pathname === "/"
    ? new URL(`${HOME_ROUTE}${url.search}`, url)
    : null;
}
